import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useCallback, useMemo } from 'react';
import { useLatest } from 'react-use';

import { useSettings } from '../api';

export const useSettingValues = () => {
  const { projectId } = useScopeIds();
  const { data: projectSettings = [], isLoading: isLoadingProjectSettings } = useSettings();
  const { data: orgSettings = [], isLoading: isLoadingOrgSettings } = useSettings({
    projectId: '',
  });

  const settings = useMemo(() => {
    const settingsList = projectId ? [...orgSettings, ...projectSettings] : orgSettings;
    return settingsList.reduce<Record<string, unknown>>((acc, setting) => {
      acc[setting.key] = setting.value;
      return acc;
    }, {});
  }, [orgSettings, projectSettings, projectId]);
  const settingsRef = useLatest(settings);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  function getSettingValue<T = unknown>(key: string): T;

  function getSettingValue<T = unknown>(key: string, defaultValue: T): T;
  function getSettingValue(key: string, defaultValue?: unknown) {
    return settingsRef.current[key] ?? defaultValue;
  }
  const entities = useMemo(() => {
    return Object.entries(settings).reduce<Record<string, unknown>>((acc, [, settingValue]) => {
      if (Array.isArray(settingValue)) {
        return settingValue.reduce((acc2, value) => {
          if (typeof value === 'object' && value && 'id' in value) {
            acc2[value.id] = value.value;
            return acc2;
          }
          return acc2;
        }, acc);
      } else {
        return acc;
      }
    }, {});
  }, [settings]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  function getSettingEntityValue<T = unknown>(key: string): T;

  function getSettingEntityValue<T = unknown>(key: string, defaultValue: T): T;
  function getSettingEntityValue(key: string, defaultValue?: unknown) {
    return entities[key] ?? defaultValue;
  }

  return {
    settings,
    entities,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    getSettingEntityValue: useCallback(getSettingEntityValue, [entities]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    getSettingValue: useCallback(getSettingValue, [settingsRef]),
    isLoading: isLoadingOrgSettings || isLoadingProjectSettings,
  };
};
